import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'
import { getBrandLocationGroupId } from 'pared/utils/brand'

import { useDateFilter } from '../../dateFilter'
import { useVariables } from '../../variables'
import { IApiDataType } from '../types'

interface IDataType {
  trendLocationGroupMetricValues: {
    nodes: {
      businessQuarter: number
      businessMonth: number
      businessWeek: number
      businessWeekOfMonth: number
      metricData: Record<
        string,
        {
          name: string
          unit: string
          value: number
        }
      >
    }[]
  }
}

interface IReturnType {
  period: string
  value: number | null
}

const query = gql`
  query TrendFinancialCheckAverageData(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const priorYearQuery = gql`
  query TrendPriorFinancialCheckAverageData(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

export const farwestCheckAverageChartsConfigs = {
  period: 'string',
  currentValue: 'price',
  lastYearValue: 'price',
} as const

function getRevenueCenterName(code: string) {
  if (code.includes('revenue_center_')) {
    switch (code) {
      case 'revenue_center_check_average_online_this_year':
        return 'Online (Pick Up)'

      case 'revenue_center_check_average_takeoutcallincallcenter_this_year':
        return 'Take Out - Call in/Call Center'

      case 'revenue_center_check_average_dispatch_this_year':
        return 'Online (Dispatch)'

      default:
        return code
          .replace(/revenue_center_check_average_/g, '')
          .replace(/_this_year/g, '')
          .replace(/_([a-z])/g, (match, letter) => ` ${letter.toUpperCase()}`)
          .replace(/^[a-z]/, (letter) => letter.toUpperCase())
    }
  } else {
    return code
      .replace(/net_/g, '')
      .replace(/_([a-z])/g, (match, letter) => ` ${letter.toUpperCase()}`)
      .replace(/^[a-z]/, (letter) => letter.toUpperCase())
  }
}

const useFarwestCheckAverageCharts = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const brandLocationGroupId = getBrandLocationGroupId()

  const groupBy = variables.farwestTimeinterval?.value[0][0] || 'business_month'

  const dateList = ((groupBy: string) => {
    if (groupBy === 'business_week')
      return variables.date?.getCalendar('week', 'year')?.ids || [[]]
    else if (groupBy === 'business_quarter') return ['Q1', 'Q2', 'Q3', 'Q4']
    else return variables.date?.getCalendar('period', 'year')?.ids || [[]]
  })(groupBy)

  const metrics = variables.selectOptions?.orderMode?.value[0][0]

  const { data, loading: currentLoading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iGroupBy: groupBy,
      iFilter: {
        location_group_ids: !hasGroupBy
          ? [brandLocationGroupId]
          : groupFilter?.ids,
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: [metrics],
      },
    },
    skip: !startDate || !endDate || !metrics,
  })

  const { data: priorData, loading: priorLoading } = useQuery<IDataType>(
    priorYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter: {
          location_group_ids: !hasGroupBy
            ? [brandLocationGroupId]
            : groupFilter?.ids,
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [metrics],
          // why: yoy_offset:1 (not use_yoy) so PY membership is re-anchored to
          // the prior-year window, including stores that closed since (FW CFO
          // decision, ENG-5218): total-sales views must include closed stores
          // for periods they operated. use_yoy keeps current-window membership
          // and drops them (e.g. store 7043, closed 2026-04-02).
          yoy_offset: 1,
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
      },
      skip: !startDate || !endDate || !metrics,
    },
  )

  const loading = currentLoading || priorLoading

  return {
    data: useMemo((): IApiDataType => {
      if (
        !data?.trendLocationGroupMetricValues.nodes ||
        !priorData?.trendLocationGroupMetricValues.nodes ||
        !metrics
      )
        return null

      const metricData = data?.trendLocationGroupMetricValues.nodes.map(
        (item): IReturnType => {
          const period = ((groupBy: string) => {
            if (groupBy === 'business_week')
              return `P${item.businessMonth}W${item.businessWeekOfMonth}`
            else if (groupBy === 'business_quarter')
              return `Q${item.businessQuarter}`
            else return `P${item.businessMonth}`
          })(groupBy)

          if (
            ['DOLLAR', 'PERCENT', 'PERCENTAGE'].includes(
              item.metricData?.[metrics].unit,
            )
          )
            return {
              period,
              value: item.metricData?.[metrics].value
                ? item.metricData?.[metrics].value * 100
                : null,
            }

          return {
            period,
            value: item.metricData?.[metrics].value || null,
          }
        },
      )

      const priorMetricData =
        priorData?.trendLocationGroupMetricValues.nodes.map(
          (item): IReturnType => {
            const period = ((groupBy: string) => {
              if (groupBy === 'business_week')
                return `P${item.businessMonth}W${item.businessWeekOfMonth}`
              else if (groupBy === 'business_quarter')
                return `Q${item.businessQuarter}`
              else return `P${item.businessMonth}`
            })(groupBy)

            if (
              ['DOLLAR', 'PERCENT', 'PERCENTAGE'].includes(
                item.metricData?.[metrics].unit,
              )
            )
              return {
                period,
                value: item.metricData?.[metrics].value
                  ? item.metricData?.[metrics].value * 100
                  : null,
              }

            return {
              period,
              value: item.metricData?.[metrics].value || null,
            }
          },
        )

      const displayMetric = getRevenueCenterName(metrics)

      return {
        source: dateList
          .map((date) => {
            const currentData = metricData.find((d) => d.period === date)
            const sameDaylyData = priorMetricData.find((d) => d.period === date)

            return {
              period: `${date} - ${displayMetric}`,
              currentValue: currentData?.value,
              lastYearValue: sameDaylyData?.value,
            }
          })
          .filter((d) => d.period),
      }
    }, [data, priorData, startDate, endDate]),
    loading,
  }
}

export default useFarwestCheckAverageCharts
